import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import authService from '../../auth/authService';
import { SAKTI_DEFAULTS } from '../apiConfig';
import SaktiApiService from '../services/saktiApiService';
import {
  SaktiConnectionStatus,
  SaktiDashboardData,
  SaktiFilters,
  SaktiKpiTotals,
  SaktiLivePayload,
  SaktiLiveResponse,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dev-sakti.gbs.id/api/insight';

const EMPTY_KPI: SaktiKpiTotals = {
  total_bcc_cnt: 0,
  total_spb_cnt: 0,
  total_bcc_weight: 0,
  total_restan_weight: 0,
};

const selectDashboardData = (
  payload: SaktiLivePayload,
  preferredCompanyId?: number
): SaktiDashboardData | null => {
  const fallbackCompanyId =
    preferredCompanyId ??
    payload.barchart[0]?.company_id ??
    payload.kpi[0]?.company_id ??
    payload.recent_activities[0]?.company_id;

  if (!fallbackCompanyId) {
    return null;
  }

  const barchartByCompany = payload.barchart.find(
    (item) => item.company_id === fallbackCompanyId
  );
  const kpiByCompany = payload.kpi.find((item) => item.company_id === fallbackCompanyId);
  const activityByCompany = payload.recent_activities.find(
    (item) => item.company_id === fallbackCompanyId
  );

  const companyName =
    barchartByCompany?.company || kpiByCompany?.company || activityByCompany?.company || '-';

  return {
    companyId: fallbackCompanyId,
    companyName,
    kpi: kpiByCompany?.totals ?? EMPTY_KPI,
    realisasiTaksasi: barchartByCompany?.realisasi_taksasi ?? [],
    realisasiHk: barchartByCompany?.realisasi_hk ?? [],
    recentActivities: activityByCompany?.recent_activities ?? [],
  };
};

interface UseSaktiLiveDashboardResult {
  dashboard: SaktiDashboardData | null;
  loading: boolean;
  error: string | null;
  connectionStatus: SaktiConnectionStatus;
  lastUpdatedAt: string | null;
  reconnect: () => void;
}

export const useSaktiLiveDashboard = (
  filters: SaktiFilters
): UseSaktiLiveDashboardResult => {
  const [dashboard, setDashboard] = useState<SaktiDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] =
    useState<SaktiConnectionStatus>('connecting');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [reconnectCount, setReconnectCount] = useState(0);

  const abortControllerRef = useRef<AbortController | null>(null);
  const dashboardRef = useRef<SaktiDashboardData | null>(null);

  useEffect(() => {
    dashboardRef.current = dashboard;
  }, [dashboard]);

  const streamPath = useMemo(
    () => SaktiApiService.buildLiveDashboardPath(filters),
    [filters]
  );

  const reconnect = useCallback(() => {
    setReconnectCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const token = authService.getToken();
    if (!token) {
      setLoading(false);
      setConnectionStatus('error');
      setError('Token autentikasi tidak ditemukan. Silakan login ulang.');
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(!dashboardRef.current);
    setError(null);
    setConnectionStatus(dashboardRef.current ? 'reconnecting' : 'connecting');

    fetchEventSource(`${API_BASE_URL}${streamPath}`, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
      openWhenHidden: true,
      async onopen(response) {
        if (!response.ok) {
          throw new Error(`Gagal membuka stream dashboard (${response.status})`);
        }
        setConnectionStatus('connected');
      },
      onmessage(message) {
        if (message.event && message.event !== 'dashboard_update' && message.event !== 'message') {
          return;
        }

        if (!message.data || message.data === '[DONE]') {
          return;
        }

        try {
          const parsedPayload = JSON.parse(message.data) as SaktiLiveResponse;
          if (!parsedPayload.success || !parsedPayload.data) {
            return;
          }

          const selectedData = selectDashboardData(parsedPayload.data, filters.company_id);
          if (selectedData) {
            setDashboard(selectedData);
          }

          setLastUpdatedAt(new Date().toISOString());
          setConnectionStatus('connected');
          setLoading(false);
          setError(null);
        } catch (parseError) {
          setError(
            parseError instanceof Error
              ? parseError.message
              : 'Terjadi kesalahan saat memproses data live dashboard'
          );
          setLoading(false);
        }
      },
      onclose() {
        if (!controller.signal.aborted) {
          setConnectionStatus('reconnecting');
          throw new Error('Koneksi stream ditutup oleh server');
        }
      },
      onerror(streamError) {
        if (!controller.signal.aborted) {
          setConnectionStatus('reconnecting');
          setError(
            streamError instanceof Error
              ? streamError.message
              : 'Koneksi live dashboard terputus'
          );
          setLoading(false);
        }

        return SAKTI_DEFAULTS.RECONNECT_INTERVAL_MS;
      },
    }).catch((streamError) => {
      if (!controller.signal.aborted) {
        setConnectionStatus('error');
        setError(
          streamError instanceof Error
            ? streamError.message
            : 'Tidak dapat terhubung ke live dashboard'
        );
        setLoading(false);
      }
    });

    return () => {
      controller.abort();
    };
  }, [streamPath, filters.company_id, reconnectCount]);

  return {
    dashboard,
    loading,
    error,
    connectionStatus,
    lastUpdatedAt,
    reconnect,
  };
};

export default useSaktiLiveDashboard;

